import ScreenShareInvertMouseWheelCheckbox from './ScreenShareInvertMouseWheelCheckbox'
import { useContext, useState } from 'react'
import useUserContextRef from '../../services/useUserContextRef'
import { useTranslation } from 'react-i18next'
import useMediaSelectionActions from '../../actions/useMediaSelectionActions'
import useMediaSourceViews from '../../actions/useMediaSourceViews'
import { isNullMediaDeviceId, sleep } from '../../utils'
import { UserContext, UserContextType } from '../../UserContext'
import { AppContext, AppContextType } from '../../AppContext'
import useAnalytics from '../../services/useAnalytics'
import { AnalyticsEvents } from '../../services/AnalyticsService'
import {
    ScreenFrameRateSelect,
    ScreenQualitySelect,
} from '@/components/SessionSettings/internal/video/sections/ScreenShareSection'
import { Button } from '@/UIComponents2/Button'
import { Checkbox } from '@/UIComponents2/Checkbox'

export default function BottomTray({ selectedDesktopId, onClose }: { selectedDesktopId: string; onClose: () => void }) {
    const { state: appState } = useContext(AppContext) as AppContextType
    const { state } = useContext(UserContext) as UserContextType

    const { userStateRef } = useUserContextRef()

    const { logUserActivityWithContext } = useAnalytics()

    const { t } = useTranslation()

    const { setMediaSelection, setGlobalOutputDeviceToMuseAudioShare, toggleAudioDeviceSelection } =
        useMediaSelectionActions()

    const { audioInputsClassifiedForUi } = useMediaSourceViews()

    const systemAudioInput = audioInputsClassifiedForUi.system[0] || null

    const isSystemAudioBeingShared = (() => {
        let hasMatch = false
        state.mediaSelection.audioInputs.forEach((a) => {
            const match = audioInputsClassifiedForUi.system.find((b) => b.deviceId === a.deviceId)
            if (match) {
                hasMatch = true
            }
        })
        return hasMatch
    })()

    const [doShareComputerAudio, set_doShareComputerAudio] = useState(isSystemAudioBeingShared)

    const onShareClickHandler = async () => {
        setMediaSelection({ desktop: selectedDesktopId })

        if (doShareComputerAudio && systemAudioInput) {
            const isChecked = state.mediaSelection.audioInputs
                .map((i) => i.deviceId)
                .includes(systemAudioInput.deviceId)

            if (!isChecked) {
                toggleAudioDeviceSelection(systemAudioInput)
                // nasty: wait for `systemAudioInput` to be propagated in the UserContext.
                for (let i = 0; i < 100; ++i) {
                    await sleep(10)
                    let match = userStateRef.current.mediaStreams.audioInputs.find(
                        (a) => a.info.deviceId === systemAudioInput.deviceId
                    )
                    if (match) {
                        break
                    }
                }

                // this call requires `mediaSources.audioInputs` to contain `systemAudioInput`.
                setGlobalOutputDeviceToMuseAudioShare({ undoWhenScreenShareEnds: true })
            }
        } else if (!doShareComputerAudio && systemAudioInput) {
            const isChecked = state.mediaSelection.audioInputs
                .map((i) => i.deviceId)
                .includes(systemAudioInput.deviceId)
            if (isChecked) {
                toggleAudioDeviceSelection(systemAudioInput)
            }
        }

        logUserActivityWithContext(AnalyticsEvents.CLICKED_SHARE_SCREEN_CONFIRM_BUTTON, {
            screenShareResolution: appState.appSettings.screenShareDesktopVideoOptions?.optionHeight || 0,
            screenShareFramerate: appState.appSettings.screenShareDesktopVideoOptions?.optionFramerate || 0,
            screenShareAspectRatio: appState.appSettings.screenShareDesktopVideoOptions?.optionAspectRatio || 0,
            screenShareAudio: doShareComputerAudio,
        })
        onClose()
    }

    const onShareComputerAudioCheckboxClick = () => {
        set_doShareComputerAudio((b) => !b)
    }

    return (
        <div className="flex items-end justify-between mt-4 mb-6 mx-6 gap-3">
            <div className="flex w-full items-center gap-3">
                <div className="flex-1">
                    <ScreenQualitySelect />
                    {/* TODO */}
                    {/* <div className="flex items-center mb-1"> */}
                    {/* {(appState.appSettings.screenShareDesktopVideoOptions?.optionHeight || 0) >
                            screen_share_resolution_warning_threshold && (
                            <ModalCompatTooltip
                                content={"Using high-resolution screen sharing may impact your computer's performance."}
                                direction={'bottom'}
                            >
                                <WarningAmberIcon style={{ width: 20, color: '#f9cc04' }} />
                            </ModalCompatTooltip>
                        )} */}
                    {/* </div> */}
                </div>
                <div className="flex-1">
                    <ScreenFrameRateSelect />
                </div>
                <div className="flex flex-col mx-2 gap-2 pt-4">
                    {systemAudioInput && (
                        <ShareComputerAudioCheckbox
                            doShareComputerAudio={doShareComputerAudio}
                            onShareComputerAudioCheckboxClick={onShareComputerAudioCheckboxClick}
                        />
                    )}
                    <ScreenShareInvertMouseWheelCheckbox />
                </div>
            </div>
            <div className="flex justify-end w-50">
                <Button className="w-full" variant="primary" onClick={onShareClickHandler}>
                    {isNullMediaDeviceId(selectedDesktopId) ? t('OK') : t('Share Screen')}
                </Button>
            </div>
        </div>
    )
}

function ShareComputerAudioCheckbox({
    doShareComputerAudio,
    onShareComputerAudioCheckboxClick,
}: {
    doShareComputerAudio: boolean
    onShareComputerAudioCheckboxClick: () => void
}) {
    return (
        <Checkbox
            checked={doShareComputerAudio}
            label="Share computer audio"
            onChange={onShareComputerAudioCheckboxClick}
        />
    )
}
